import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useKitchen } from "../contexts/KitchenContext";
import { usersAPI } from "../utils/api";
import {
  LayoutDashboard, Users, Truck, Shield, Container,
  Package, ShoppingCart, CheckSquare, Settings, BarChart3,
  LogOut, Bell, Menu, X, ChevronDown, ChevronRight, Key,
  UtensilsCrossed, UserCheck, Briefcase, Wrench, Activity, Sparkles, CalendarCheck,
  Store, School, IndianRupee, CalendarDays, Star, Navigation, ClipboardList, TrendingUp, Phone
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_9e7e0d6e-4f16-4092-b7c7-18a05794ec67/artifacts/w0k6jgr2_WhatsApp%20Image%202026-04-07%20at%2017.12.34.jpeg";

const navItems = [
  // ── DASHBOARD ───────────────────────────────────────────────────────────
  { path: "/", icon: LayoutDashboard, label: "Dashboard", permission: null },

  // ── TASK MANAGER ────────────────────────────────────────────────────────
  { path: "/tasks", icon: CheckSquare, label: "Task Manager", permission: null },

  // ── CALL CENTRE ─────────────────────────────────────────────────────────
  { path: "/call-centre", icon: Phone, label: "Call Centre", permission: "call_centre" },

  // ── SECURITY / GATE ─────────────────────────────────────────────────────
  {
    icon: Shield, label: "Security / Gate", permission: "security_view",
    children: [
      { path: "/security",  label: "Entry / Exit", perm: "security_create", subKey: "security_entry_exit" },
      { path: "/gate-log",  label: "Gate Log",     perm: "security_view",   subKey: "security_gate_log"   },
    ]
  },

  // ── DAILY OPERATIONS ────────────────────────────────────────────────────
  {
    icon: Activity, label: "Daily Operations", permission: null,
    children: [
      { path: "/daily-entry",    label: "Daily Entry Hub",          subKey: "ops_daily_entry"    },
      { path: "/school-indent",  label: "School Indent for Cooking",subKey: "ops_school_indent"  },
      { path: "/reconciliation", label: "MDM Daily Reconciliation", subKey: "ops_reconciliation" },
    ]
  },

  // ── VESSELS ─────────────────────────────────────────────────────────────
  {
    icon: Container, label: "Vessels", permission: "vessels_view",
    children: [
      { path: "/vessels",           label: "Vessel Management", subKey: "ops_vessels"      },
      { path: "/vessels?tab=entry", label: "Vessel Entry",      subKey: "ops_vessel_entry" },
    ]
  },

  // ── KITCHEN & MENU ──────────────────────────────────────────────────────
  {
    icon: UtensilsCrossed, label: "Kitchen & Menu", permission: "menu_view",
    children: [
      { path: "/production",            label: "Production Planning",  subKey: "kitchen_production"  },
      { path: "/menu?tab=items",        label: "Menu Items",           subKey: "kitchen_menu_items"  },
      { path: "/menu?tab=recipes",      label: "Recipes",              subKey: "kitchen_recipes"     },
      { path: "/menu?tab=plan",         label: "Daily Plan",           subKey: "kitchen_daily_plan"  },
      { path: "/menu?tab=calculator",   label: "Stock Calculator",     subKey: "kitchen_calculator"  },
      { path: "/recipe-cost",           label: "Recipe Cost"                                         },
    ]
  },

  // ── STORES ──────────────────────────────────────────────────────────────
  {
    icon: Package, label: "Stores", permission: "stores_view",
    children: [
      { path: "/stores?tab=inward",        label: "Stock Inward",      subKey: "stores_inward"        },
      { path: "/stores?tab=issue",         label: "Stock Issued",      subKey: "stores_issued"        },
      { path: "/stores?tab=reject",        label: "Reject Stock",      subKey: "stores_reject"        },
      { path: "/stores?tab=outward",       label: "Stock Outward",     subKey: "stores_outward"       },
      { path: "/stores?tab=alerts",        label: "Min Stock Alerts",  subKey: "stores_alerts"        },
      { path: "/operations?tab=vehicles",  label: "Vehicle Usage",     subKey: "stores_vehicle_usage" },
      { path: "/stores?tab=indent",        label: "Purchase Request",  subKey: "stores_purchase_req"  },
      { path: "/stores?tab=summary",       label: "Stock Summary",     subKey: "stores_summary"       },
    ]
  },

  // ── PURCHASE & VENDORS ──────────────────────────────────────────────────
  {
    icon: ShoppingCart, label: "Purchase & Vendors", permission: "purchase_view",
    children: [
      { path: "/purchase?tab=indents", label: "Indents Received",   subKey: "purchase_indents" },
      { path: "/purchase?tab=orders",  label: "Purchase Orders",    subKey: "purchase_orders"  },
      { path: "/vendors",              label: "Vendor Management",  subKey: "purchase_vendors" },
      { path: "/purchase?tab=quotes",  label: "Quote Analysis",     subKey: "purchase_quotes"  },
    ]
  },

  // ── LOGISTICS ───────────────────────────────────────────────────────────
  {
    icon: Truck, label: "Logistics", permission: "logistics_view",
    children: [
      { path: "/logistics",                          label: "Route Master",       subKey: "logistics_routes"       },
      { path: "/logistics?tab=routes",               label: "Vehicles Assigned",  subKey: "logistics_routes"       },
      { path: "/logistics?tab=school_assignment",    label: "School Assignment",  subKey: "logistics_routes"       },
      { path: "/logistics?tab=coordinators",         label: "Route Coordinators", subKey: "logistics_coordinators" },
      { path: "/logistics?tab=schools",              label: "School Master",      subKey: "logistics_schools"      },
      { path: "/logistics?tab=vehicles_drivers",     label: "Vehicles & Drivers", subKey: "logistics_routes"       },
      { path: "/logistics?tab=temp",                 label: "Temp Assignments",   subKey: "logistics_temp"         },
      { path: "/logistics?tab=maintenance",          label: "Vehicle Maintenance",subKey: "logistics_maintenance"  },
      { path: "/logistics?tab=fuel",                 label: "Fuel Log",           subKey: "logistics_fuel"         },
      { path: "/logistics?tab=reconciliation",       label: "Reconciliation",     subKey: null                     },
      { path: "/route-planner",                      label: "Route Planner",      subKey: "route_planner", perm: "route_planner" },
      { path: "/delivery",                           label: "Delivery Tracking",  subKey: "logistics_delivery"     },
      { path: "/vehicle-tracking",                   label: "Vehicle GPS",        subKey: null                     },
    ]
  },

  // ── FINANCE ─────────────────────────────────────────────────────────────
  {
    icon: IndianRupee, label: "Finance", permission: "admin_view",
    children: [
      { path: "/accounts",      label: "Accounts & Billing",  subKey: "finance_accounts" },
      { path: "/utility-costs", label: "Utility Costs",       subKey: "finance_utility"  },
    ]
  },

  // ── PEOPLE & HR ─────────────────────────────────────────────────────────
  {
    icon: Users, label: "People & HR", permission: "hr_view",
    children: [
      { path: "/hr?tab=employees",      label: "Employee Master",  subKey: "hr_employees"  },
      { path: "/hr?tab=workers",        label: "Workers List",     subKey: "hr_workers"    },
      { path: "/hr?tab=subcontractors", label: "Sub-Contractors"                           },
      { path: "/hr?tab=departments",    label: "Departments"                               },
      { path: "/attendance",            label: "Attendance",       subKey: "hr_attendance" },
      { path: "/operations?tab=ot",     label: "OT Reports",       subKey: "hr_ot"         },
      { path: "/holidays",              label: "Holiday Calendar", subKey: "hr_holidays"   },
    ]
  },

  // ── ASSETS & QUALITY ────────────────────────────────────────────────────
  {
    icon: Wrench, label: "Assets & Quality", permission: null,
    children: [
      { path: "/assets",               label: "Asset Management",   subKey: "assets_main"        },
      { path: "/quality",              label: "Quality Inspection", subKey: "assets_quality"     },
      { path: "/inspector-visits",     label: "Inspection Visits",  subKey: "assets_inspections" },
      { path: "/operations?tab=field", label: "Field Visits",       subKey: "assets_field"       },
      { path: "/compliance",           label: "School Compliance",  subKey: "assets_compliance"  },
    ]
  },

  // ── REPORTS & AI ────────────────────────────────────────────────────────
  {
    icon: Sparkles, label: "Reports & AI", permission: "reports_view",
    children: [
      { path: "/ai",           label: "AI Analytics (Manna)", subKey: "reports_ai"       },
      { path: "/reports",      label: "Reports",              subKey: "reports_main"     },
      { path: "/deletion-log", label: "Deletion Log",         subKey: "reports_deletion" },
    ]
  },

  // ── SETTINGS ────────────────────────────────────────────────────────────
  {
    icon: Settings, label: "Settings", permission: "users_view",
    children: [
      { path: "/users",          label: "User Management",  subKey: "settings_users"      },
      { path: "/admin",          label: "Admin Settings",   subKey: "settings_admin"      },
      { path: "/report-formats", label: "Report Formats",   subKey: "settings_report_fmt" },
      { path: "/review",         label: "Review Required",  badge: true                   },
    ]
  },
];

function NavItem({ item, collapsed, onNavigate, reviewCount = 0 }) {
  const { user } = useAuth();
  const location = useLocation();

  const hasPermission = !item.permission || user?.is_admin || user?.permissions?.[item.permission];

  // Compute isActive for group items BEFORE hooks
  const isActiveGroup = item.children
    ? item.children.some(c => {
        const childPath = c.path.split("?")[0];
        return location.pathname === childPath || location.pathname.startsWith(childPath + "/");
      })
    : false;

  // Hooks must be at top level — always called unconditionally
  const [open, setOpen] = useState(isActiveGroup);
  useEffect(() => { if (isActiveGroup) setOpen(true); }, [location.pathname]);

  if (!hasPermission) return null;

  if (item.children) {
    return (
      <div>
        <button
          onClick={() => setOpen(!open)}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold transition-all
            ${isActiveGroup ? "bg-[#E8956D]/15 text-[#E8956D]" : "text-[#5C5147] hover:bg-[#F4F3F0]"}`}
        >
          <item.icon size={17} className="flex-shrink-0" />
          {!collapsed && (
            <>
              <span className="flex-1 text-left text-[13px]">{item.label}</span>
              <ChevronDown size={13} className={`transition-transform ${open ? "" : "-rotate-90"}`} />
            </>
          )}
        </button>
        {open && !collapsed && (
          <div className="ml-7 mt-0.5 mb-1 border-l-2 border-[#F4F3F0] pl-3 space-y-0.5">
            {item.children.filter(child => {
              // 1. Check module-level perm (e.g. security_create)
              const moduleOk = !child.perm || user?.is_admin || !!user?.permissions?.[child.perm];
              // 2. Check sub-module view permission: if explicitly set to false → hide
              //    (if not set at all → default visible, backward compatible)
              const subOk = !child.subKey || user?.is_admin ||
                user?.permissions?.[`sub_${child.subKey}_view`] !== false;
              return moduleOk && subOk;
            }).map(child => (
              <NavLink
                key={child.path}
                to={child.path}
                onClick={onNavigate}
                className={() => {
                  const pathMatch = location.pathname === child.path.split("?")[0];
                  return `block px-2.5 py-1.5 rounded-md text-xs font-medium transition-all
                  ${pathMatch ? "bg-[#E8956D]/10 text-[#E8956D] font-semibold" : "text-[#8A7E73] hover:text-[#5C5147] hover:bg-[#F4F3F0]"}`;
                }}
              >
                <span className="flex items-center justify-between">
                  {child.label}
                  {child.badge && reviewCount > 0 && (
                    <span className="bg-[#D98032] text-white text-[9px] font-extrabold px-1.5 py-0.5 rounded-full min-w-[16px] text-center">{reviewCount}</span>
                  )}
                </span>
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={item.path}
      onClick={onNavigate}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all
        ${isActive ? "bg-[#E8956D]/15 text-[#E8956D]" : "text-[#5C5147] hover:bg-[#F4F3F0]"}`
      }
    >
      <item.icon size={17} className="flex-shrink-0" />
      {!collapsed && (
        <>
          <span className="flex-1">{item.label}</span>
          {reviewCount > 0 && (
            <span className="bg-[#D98032] text-white text-[10px] font-extrabold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
              {reviewCount}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const kitchenCtx = useKitchen();
  // Read kitchens directly from user object (comes in login/me response — always fresh)
  const kitchens = (user?.kitchens?.length > 0 ? user.kitchens : kitchenCtx?.kitchens) || [];
  const selectedKitchen = kitchenCtx?.selectedKitchen ?? "";
  const changeKitchen = kitchenCtx?.changeKitchen || (() => {});
  const isKitchenLocked = kitchenCtx?.isKitchenLocked || false;

  // Load kitchens into context once after login
  useEffect(() => {
    if (user?.kitchens?.length > 0 && kitchenCtx?.loadKitchens) {
      kitchenCtx.loadKitchens(user.kitchens);
    } else if (kitchenCtx?.kitchens?.length === 0) {
      // Admin users may not have kitchens on user object — fetch directly
      usersAPI.getKitchens().then(r => {
        const ks = r.data || [];
        if (ks.length > 0 && kitchenCtx?.loadKitchens) kitchenCtx.loadKitchens(ks);
      }).catch(() => {});
    }
  }, [user?.id]);
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [changePwdModal, setChangePwdModal] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: "", next: "", confirm: "" });
  const [pwdError, setPwdError] = useState("");
  const [pwdSuccess, setPwdSuccess] = useState("");
  const [pushEnabled, setPushEnabled] = useState(false);
  // Kitchen dropdown state — custom dropdown, NOT native <select>
  const [kitchenDropOpen, setKitchenDropOpen] = useState(false);
  const kitchenDropRef = useRef(null);

  // Review Required badge count
  const [reviewCount, setReviewCount] = useState(0);
  useEffect(() => {
    const fetchReviewCount = () => {
      const API = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("manna_access_token");
      if (!token) return;
      fetch(`${API}/api/review/pending-count`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()).then(d => setReviewCount(d.count || 0)).catch(() => {});
    };
    fetchReviewCount();
    const interval = setInterval(fetchReviewCount, 60000);
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    const handler = (e) => { if (kitchenDropRef.current && !kitchenDropRef.current.contains(e.target)) setKitchenDropOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleKitchenSelect = (id) => {
    changeKitchen(id);
    setKitchenDropOpen(false);
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const { data } = await usersAPI.getNotifications(10);
      setNotifs(data);
      setNotifCount(data.filter(n => !n.is_read).length);
    } catch (e) { /* fetch error silenced in production */ }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleLogoClick = () => navigate("/");

  // Push notification toggle
  useEffect(() => {
    import("../utils/pushNotifications").then(({ isPushSubscribed }) => {
      isPushSubscribed().then(sub => setPushEnabled(sub));
    });
  }, []);

  const togglePush = async () => {
    const { subscribeToPush, unsubscribeFromPush, isPushSubscribed } = await import("../utils/pushNotifications");
    if (pushEnabled) {
      await unsubscribeFromPush();
      setPushEnabled(false);
    } else {
      const ok = await subscribeToPush(user?.id || "");
      setPushEnabled(ok);
    }
  };

  const handleChangePwd = async () => {
    setPwdError("");
    if (pwdForm.next !== pwdForm.confirm) { setPwdError("New passwords don't match"); return; }
    if (pwdForm.next.length < 6) { setPwdError("Password must be at least 6 characters"); return; }
    try {
      const { default: axios } = await import("axios");
      const API = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${API}/api/auth/change-password`, {
        current_password: pwdForm.current,
        new_password: pwdForm.next
      }, { withCredentials: true });
      setPwdSuccess("Password changed successfully!");
      setTimeout(() => { setChangePwdModal(false); setPwdForm({ current: "", next: "", confirm: "" }); setPwdSuccess(""); }, 2000);
    } catch (e) {
      setPwdError(e?.response?.data?.detail || "Failed to change password");
    }
  };

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-[#E5E1DB]">
        <img
          src={LOGO_URL}
          alt="Manna"
          className="h-9 w-9 rounded-lg object-contain cursor-pointer flex-shrink-0"
          onClick={handleLogoClick}
          data-testid="logo-home-btn"
        />
        {!collapsed && (
          <div className="cursor-pointer" onClick={handleLogoClick}>
            <h1 className="font-extrabold font-[Manrope] text-[#3A322C] text-base leading-tight">Manna Trust</h1>
            <p className="text-[10px] text-[#8A7E73]">Operations Platform</p>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {navItems.map((item, i) => (
          <NavItem key={i} item={item} collapsed={collapsed} onNavigate={() => setMobileOpen(false)} reviewCount={item.badge || item.children?.some(c => c.badge) ? reviewCount : 0} />
        ))}
      </nav>

      {/* User */}
      <div className="border-t border-[#E5E1DB] p-3">
        <div className={`flex items-center gap-3 px-2 py-2 rounded-lg ${collapsed ? "justify-center" : ""}`}>
          <div className="w-8 h-8 rounded-full bg-[#E8956D]/20 flex items-center justify-center flex-shrink-0">
            <span className="text-[#E8956D] font-bold text-xs">{user?.name?.[0]?.toUpperCase()}</span>
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#3A322C] truncate">{user?.name}</p>
              <p className="text-xs text-[#8A7E73] truncate">{user?.role_name || (user?.is_admin ? "Admin" : "User")}</p>
            </div>
          )}
          {!collapsed && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => { setChangePwdModal(true); setPwdError(""); setPwdSuccess(""); }}
                className="text-[#8A7E73] hover:text-[#E8956D] transition-colors p-1"
                title="Change Password"
                data-testid="change-password-btn"
              >
                <Key size={15} />
              </button>
              <button
                onClick={handleLogout}
                className="text-[#8A7E73] hover:text-[#C2624E] transition-colors p-1"
                data-testid="logout-button"
                title="Logout"
              >
                <LogOut size={15} />
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-[#FAFAF8]">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col bg-white border-r border-[#E5E1DB] transition-all duration-300 ${collapsed ? "w-16" : "w-60"}`}
        style={{ position: "fixed", top: 0, left: 0, height: "100vh", zIndex: 40 }}
      >
        {sidebarContent}
        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-6 w-6 h-6 bg-white border border-[#E5E1DB] rounded-full flex items-center justify-center shadow-sm hover:border-[#E8956D] transition-colors"
        >
          {collapsed ? <ChevronRight size={12} className="text-[#5C5147]" /> : <ChevronDown size={12} className="text-[#5C5147] rotate-90" />}
        </button>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="relative z-50 w-64 bg-white h-full flex flex-col border-r border-[#E5E1DB]">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-4 right-4 text-[#8A7E73] hover:text-[#3A322C]"
            >
              <X size={20} />
            </button>
            {sidebarContent}
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className={`flex-1 flex flex-col min-h-screen transition-all duration-300 ${collapsed ? "lg:ml-16" : "lg:ml-60"}`}>
        {/* Top Bar */}
        <header className="bg-white/80 backdrop-blur-xl border-b border-[#E5E1DB] sticky top-0 z-30 px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setMobileOpen(true)}
            className="lg:hidden p-2 rounded-lg text-[#5C5147] hover:bg-[#F4F3F0]"
          >
            <Menu size={20} />
          </button>
          <div className="lg:hidden flex items-center gap-2">
            <img src={LOGO_URL} alt="Manna" className="h-7 w-7 rounded object-contain" onClick={handleLogoClick} />
            <span className="font-bold text-[#3A322C] text-sm">Manna Trust</span>
          </div>
          <div className="hidden lg:block" />

          <div className="flex items-center gap-3">
            {/* Global Kitchen Selector */}
            {isKitchenLocked ? (
              /* Single-kitchen user: locked badge — no interaction needed */
              <div className="flex items-center gap-2 rounded-lg px-3 py-1.5 bg-[#E8956D]/15"
                data-testid="global-kitchen-select"
                title="Your kitchen is fixed to your profile">
                <span className="text-[10px] font-bold uppercase text-[#8A7E73] hidden sm:block">Kitchen:</span>
                <span className="text-sm font-semibold text-[#E8956D] max-w-[140px] truncate">
                  {kitchens[0]?.name || "Assigned Kitchen"}
                </span>
                <span className="text-[10px] bg-[#E8956D]/20 text-[#E8956D] px-1.5 py-0.5 rounded-full font-bold">FIXED</span>
              </div>
            ) : (
              /* Multi-kitchen / Admin: dropdown showing only their kitchens */
              <div className="relative" ref={kitchenDropRef}>
                <button
                  onClick={() => setKitchenDropOpen(!kitchenDropOpen)}
                  className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${selectedKitchen ? "bg-[#E8956D]/15 text-[#E8956D]" : "bg-[#F4F3F0] text-[#3A322C]"}`}
                  data-testid="global-kitchen-select"
                >
                  <span className="text-[10px] font-bold uppercase text-[#8A7E73] hidden sm:block">Kitchen:</span>
                  <span className="max-w-[140px] truncate">
                    {selectedKitchen ? (kitchens.find(k => k.id === selectedKitchen)?.name || "Selected") : "All Kitchens"}
                  </span>
                  <ChevronDown size={13} className={`text-[#8A7E73] transition-transform ${kitchenDropOpen ? "rotate-180" : ""}`} />
                </button>
                {kitchenDropOpen && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-[#E5E1DB] rounded-xl shadow-lg z-50 min-w-[200px] overflow-hidden animate-fade-in-up">
                    <div className="py-1">
                      {/* "All Kitchens" option only for admin/multi-kitchen */}
                      {(user?.is_admin || kitchens.length > 1) && (
                        <button
                          onClick={() => handleKitchenSelect("")}
                          className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 transition-colors ${!selectedKitchen ? "bg-[#E8956D]/10 text-[#E8956D] font-bold" : "text-[#5C5147] hover:bg-[#F4F3F0]"}`}
                        >
                          {!selectedKitchen && <span className="text-[#E8956D]">✓</span>}
                          {selectedKitchen && <span className="w-4" />}
                          All Kitchens
                        </button>
                      )}
                      {kitchens.map(k => (
                        <button
                          key={k.id}
                          onClick={() => handleKitchenSelect(k.id)}
                          className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 transition-colors ${selectedKitchen === k.id ? "bg-[#E8956D]/10 text-[#E8956D] font-bold" : "text-[#5C5147] hover:bg-[#F4F3F0]"}`}
                        >
                          {selectedKitchen === k.id && <span className="text-[#E8956D]">✓</span>}
                          {selectedKitchen !== k.id && <span className="w-4" />}
                          {k.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => { setShowNotifs(!showNotifs); fetchNotifications(); }}
                className="relative p-2 rounded-lg text-[#5C5147] hover:bg-[#F4F3F0] transition-colors"
                data-testid="notifications-btn"
              >
                <Bell size={20} />
                {notifCount > 0 && (
                  <span className="absolute top-1 right-1 w-4 h-4 bg-[#C2624E] rounded-full text-white text-[10px] flex items-center justify-center font-bold">
                    {notifCount > 9 ? "9+" : notifCount}
                  </span>
                )}
              </button>

              {showNotifs && (
                <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-[#E5E1DB] rounded-xl shadow-lg z-50 overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E1DB]">
                    <h3 className="font-bold text-sm text-[#3A322C]">Notifications</h3>
                    <div className="flex items-center gap-2">
                      <button onClick={togglePush}
                        className={`text-[10px] font-bold px-2 py-1 rounded-full border transition-colors ${pushEnabled ? "border-[#637C64] bg-[#EDF2EE] text-[#637C64]" : "border-[#E5E1DB] text-[#8A7E73] hover:border-[#E8956D] hover:text-[#E8956D]"}`}>
                        {pushEnabled ? "Push ON" : "Push OFF"}
                      </button>
                      <button
                        onClick={() => { usersAPI.markAllRead(); setNotifCount(0); setNotifs(n => n.map(x => ({...x, is_read: true}))); }}
                        className="text-xs text-[#E8956D] hover:underline"
                      >Mark all read</button>
                    </div>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifs.length === 0 ? (
                      <p className="text-center text-sm text-[#8A7E73] py-6">No notifications</p>
                    ) : notifs.map(n => (
                      <div
                        key={n.id}
                        className={`px-4 py-3 border-b border-[#F4F3F0] text-sm ${!n.is_read ? "bg-[#FDF4EB]" : ""}`}
                      >
                        <p className="text-[#3A322C]">{n.message}</p>
                        <p className="text-[10px] text-[#8A7E73] mt-1">
                          {n.type?.toUpperCase()} · {n.module}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* User avatar */}
            <div className="w-8 h-8 rounded-full bg-[#E8956D]/20 flex items-center justify-center">
              <span className="text-[#E8956D] font-bold text-xs">{user?.name?.[0]?.toUpperCase()}</span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 md:p-6 overflow-auto">
          {children}
        </main>
      </div>

      {/* Change Password Modal */}
      {changePwdModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm animate-fade-in-up">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB]">
              <h2 className="font-bold text-[#3A322C] font-[Manrope]">Change Password</h2>
              <button onClick={() => setChangePwdModal(false)}><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              {pwdSuccess ? (
                <div className="bg-[#EDF2EE] border border-[#637C64]/30 rounded-lg px-4 py-3 text-sm text-[#637C64] text-center font-medium">{pwdSuccess}</div>
              ) : (
                <>
                  <div>
                    <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Current Password</label>
                    <input type="password" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={pwdForm.current} onChange={e => setPwdForm(f => ({ ...f, current: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">New Password</label>
                    <input type="password" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={pwdForm.next} onChange={e => setPwdForm(f => ({ ...f, next: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Confirm New Password</label>
                    <input type="password" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={pwdForm.confirm} onChange={e => setPwdForm(f => ({ ...f, confirm: e.target.value }))} />
                  </div>
                  {pwdError && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{pwdError}</p>}
                  <div className="flex gap-3">
                    <button onClick={() => setChangePwdModal(false)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147] hover:bg-[#F4F3F0]">Cancel</button>
                    <button onClick={handleChangePwd} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C] hover:bg-[#D48661]" data-testid="confirm-change-pwd-btn">Change</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
